import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './Attachments.css';

const Attachments = ({ caseData, onBack, token }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(null);
  
  // Debug log
  useEffect(() => {
    console.log('Attachments component mounted with caseData:', caseData);
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    file: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [filePreview, setFilePreview] = useState(null);

  // Fetch attachments for this case
  const fetchAttachments = async () => {
    try {
      setLoading(true);
      
      // Use case_id from caseData if available, otherwise use 0
      const caseId = caseData && caseData.id ? caseData.id : 0;
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/get/attachment/${caseId}`;
      console.log('Fetching attachments from:', apiUrl);
      console.log('Case ID:', caseId);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Attachments response:', data);

      if (response.ok && data.data && Array.isArray(data.data)) {
        setAttachments(data.data);
      } else {
        setAttachments([]);
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, file: file });
      
      // Create preview info
      setFilePreview({
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB',
        type: file.type
      });
    }
  };

  // Upload attachment
  const handleUploadAttachment = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      Swal.fire({
        title: 'Missing Information',
        text: 'You must select a file to upload.',
        icon: 'warning',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setSubmitting(true);
      
      // Use case_id from caseData if available, otherwise use 0
      const caseId = caseData && caseData.id ? caseData.id : 0;
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/add/attachment`;
      console.log('Uploading attachment:', apiUrl);
      console.log('Case ID:', caseId);

      const formDataToSend = new FormData();
      formDataToSend.append('case_id', caseId);
      formDataToSend.append('upload_file', formData.file);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formDataToSend,
      });

      const data = await response.json();
      console.log('Upload attachment response:', data);
      console.log('Response status:', response.status);

      if (response.ok) {
        Swal.fire({
          title: 'Success!',
          text: 'Attachment uploaded successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        setShowUploadModal(false);
        setFormData({
          file: null
        });
        setFilePreview(null);
        fetchAttachments(); // Refresh the list
      } else {
        // Extract validation errors if available
        let errorMessage = data.message || 'Failed to upload attachment';
        
        if (data.errors) {
          // Laravel validation errors
          const errorMessages = Object.values(data.errors).flat();
          errorMessage = errorMessages.join('\n');
        }
        
        console.error('Attachment upload failed:', data);
        
        Swal.fire({
          title: 'Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error uploading attachment:', err);
      Swal.fire({
        title: 'Error!',
        text: 'Something went wrong while uploading.',
        icon: 'error',
        confirmButtonColor: '#d33'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will delete the attachment permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (!confirm.isConfirmed) return;

    try {
      setDeletingAttachment(attachmentId);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/attachments/${attachmentId}`;
      console.log('Deleting attachment:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Delete attachment response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Deleted!',
          text: 'Attachment deleted successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        fetchAttachments(); // Refresh the list
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to delete attachment',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error deleting attachment:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to delete attachment',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setDeletingAttachment(null);
    }
  };

  // Download attachment
  const handleDownloadAttachment = (attachmentUrl, fileName) => {
    const fullUrl = `${process.env.REACT_APP_API_URL}/${attachmentUrl}`;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    // Fetch attachments regardless of whether caseData exists or not
    fetchAttachments();
  }, [caseData]);

  return (
    <div className="attachments-container">
      {/* Header */}
      <div className="attachments-header">
        <button className="back-button" onClick={onBack}>
          ←
        </button>
        <h2>
          {caseData && caseData.id 
            ? `Attachments - Case #${caseData.caseNumber || caseData.id}` 
            : 'All Attachments'}
        </h2>
      </div>

      {/* Attachments List */}
      <div className="attachments-content">
        {loading ? (
          <div className="loading-state">
            <p>Loading attachments...</p>
          </div>
        ) : attachments.length > 0 ? (
          <div className="attachments-list">
            {attachments.map(attachment => {
              const date = new Date(attachment.created_at);
              const formattedDate = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: '2-digit' 
              });
              const formattedTime = date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              });

              // Get file path (API returns upload_file)
              const filePath = attachment.upload_file || attachment.file || '';
              const fileName = filePath.split('/').pop() || 'Unknown file';
              const fileExtension = fileName.split('.').pop().toLowerCase();
              const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtension);
              const fileUrl = `${process.env.REACT_APP_API_URL}/${filePath}`;

              return (
                <div key={attachment.id} className="attachment-card">
                  <div className="attachment-preview">
                    {isImage ? (
                      <img 
                        src={fileUrl} 
                        alt="Attachment preview" 
                        className="attachment-thumbnail"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="file-icon-container" style={{ display: isImage ? 'none' : 'flex' }}>
                      <span className="file-icon">📄</span>
                    </div>
                  </div>
                  
                  <div className="attachment-info">
                    <p className="attachment-filename">{fileName}</p>
                    <p className="attachment-date">Uploaded: {formattedDate} {formattedTime}</p>
                  </div>
                  
                  <div className="attachment-actions">
                    <button
                      className="action-button download-button"
                      onClick={() => handleDownloadAttachment(filePath, fileName)}
                      title="Download"
                    >
                      ⬇️
                    </button>
                    <button
                      className="action-button "
                      onClick={() => handleDeleteAttachment(attachment.id)}
                      disabled={deletingAttachment === attachment.id}
                      title="Delete"
                    >
                      {deletingAttachment === attachment.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-attachments-state">
            <p>No attachments found for this case.</p>
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <button className="fab-button" onClick={() => setShowUploadModal(true)}>
        <span className="fab-icon">📎</span>
      </button>

      {/* Upload Attachment Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content upload-attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button className="back-button" onClick={() => setShowUploadModal(false)}>
                ←
              </button>
              <h3>Upload Attachment</h3>
            </div>

            <form onSubmit={handleUploadAttachment} className="modal-body">
              {/* File Upload */}
              <div className="form-group">
                <label htmlFor="attachment-file" className="file-upload-area">
                  {filePreview ? (
                    <div className="file-preview">
                      <span className="file-icon-large">📄</span>
                      <div className="file-info">
                        <p className="file-name">{filePreview.name}</p>
                        <p className="file-size">{filePreview.size}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="upload-icon">📁</span>
                      <p>Browse and select a file</p>
                      <p className="file-hint">Documents, Images, PDFs, etc.</p>
                    </>
                  )}
                </label>
                <input
                  id="attachment-file"
                  type="file"
                  onChange={handleFileChange}
                  className="file-input"
                  style={{ display: 'none' }}
                  required
                  aria-required="true"
                />
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="submit-button upload-button"
                disabled={submitting}
              >
                {submitting ? 'Uploading...' : 'Upload'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
export default Attachments;
